import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import DeckGrid, { TileCard } from "../components/DeckGrid";
import TileEditor from "../components/TileEditor";
import SearchCommand from "../components/SearchCommand";
import DeckEditor from "../components/DeckEditor";
import ShareDialog from "../components/ShareDialog";
import DropZone from "../components/DropZone";
import TourOverlay from "../components/TourOverlay";
import { TourProvider } from "../contexts/TourContext";
import { resolveIcon } from "../components/IconPicker";
import { Plus } from "@phosphor-icons/react";

function DeckDragPreview({ deck }) {
  const Icon = resolveIcon(deck.icon);
  return (
    <div className="drag-overlay flex items-center gap-3 px-3 py-2.5 rounded-xl bg-deck-sidebar border border-white/15 shadow-xl w-[240px]">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 deck-tile"
        style={{ backgroundColor: deck.color }}
      >
        <Icon size={18} weight="fill" className="text-white" />
      </div>
      <span className="flex-1 truncate text-sm font-heading font-semibold text-white">
        {deck.name}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [decks, setDecks] = useState([]);
  const [activeDeckId, setActiveDeckId] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [view, setView] = useState("grid"); // grid | list
  const [searchOpen, setSearchOpen] = useState(false);

  const [tileEditorOpen, setTileEditorOpen] = useState(false);
  const [editingTile, setEditingTile] = useState(null);

  const [deckEditorOpen, setDeckEditorOpen] = useState(false);
  const [editingDeck, setEditingDeck] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [droppingCount, setDroppingCount] = useState(0);

  const initialized = useRef(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [loading, user, navigate]);

  // Fetch decks on mount
  useEffect(() => {
    if (!user) return;
    if (initialized.current) return;
    initialized.current = true;
    (async () => {
      try {
        const { data } = await api.get("/decks");
        setDecks(data);
        if (data.length > 0) setActiveDeckId(data[0].deck_id);
      } catch (e) {
        toast.error("Error al cargar las plataformas");
      }
    })();
  }, [user]);

  // Fetch tiles when active deck changes
  useEffect(() => {
    if (!activeDeckId) return;
    (async () => {
      try {
        const { data } = await api.get(`/decks/${activeDeckId}/tiles`);
        setTiles(data);
      } catch (e) {
        toast.error("Error al cargar los botones");
      }
    })();
  }, [activeDeckId]);

  // Cmd+K to open search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeDeck = useMemo(
    () => decks.find((d) => d.deck_id === activeDeckId) || null,
    [decks, activeDeckId]
  );

  const handleAddTile = () => {
    setEditingTile(null);
    setTileEditorOpen(true);
  };

  const handlePasteAndCreate = async () => {
    if (!activeDeckId) {
      toast.error("Crea una plataforma primero");
      return;
    }
    if (pasting) return;
    setPasting(true);
    try {
      let text = "";
      try {
        text = await navigator.clipboard.readText();
      } catch (err) {
        toast.error("Permite acceso al portapapeles para usar Pegar");
        return;
      }
      const raw = (text || "").trim();
      if (!raw) {
        toast.error("Tu portapapeles está vacío");
        return;
      }
      // Validate URL
      let urlStr = raw;
      if (!/^https?:\/\//i.test(urlStr)) {
        urlStr = "https://" + urlStr;
      }
      try {
        new URL(urlStr);
      } catch {
        toast.error("El portapapeles no contiene un URL válido");
        return;
      }

      toast.loading("Generando vista previa...", { id: "paste-create" });
      const { data: preview } = await api.get(
        `/preview?url=${encodeURIComponent(urlStr)}`
      );

      const payload = {
        deck_id: activeDeckId,
        name: (preview.site_name || preview.title || urlStr).slice(0, 40),
        url: preview.url || urlStr,
        style: "rich",
        size: "2x1",
        color: preview.theme_color || "#1A1C29",
        text_color: "#FFFFFF",
      };
      if (preview.image) payload.preview_image = preview.image;
      if (preview.title) payload.preview_title = preview.title;
      if (preview.description) payload.preview_description = preview.description;
      if (preview.theme_color) payload.theme_color = preview.theme_color;
      if (preview.site_name) payload.site_name = preview.site_name;
      if (preview.favicon) payload.favicon = preview.favicon;

      const { data: tile } = await api.post("/tiles", payload);
      setTiles((prev) => [...prev, tile]);
      toast.success(`"${tile.name}" añadido`, { id: "paste-create" });
    } catch (e) {
      toast.error("No se pudo crear la tarjeta", { id: "paste-create" });
    } finally {
      setPasting(false);
    }
  };

  const handleDropUrls = async (urls) => {
    if (!activeDeckId) {
      toast.error("Crea una plataforma primero");
      return;
    }
    if (!urls || urls.length === 0) return;
    setDroppingCount(urls.length);
    const toastId = "drop-create";
    toast.loading(`Añadiendo ${urls.length} link${urls.length > 1 ? "s" : ""}...`, {
      id: toastId,
    });

    let added = 0;
    const newTiles = [];

    for (const rawUrl of urls) {
      let urlStr = rawUrl.trim();
      if (!urlStr) continue;
      if (!/^https?:\/\//i.test(urlStr)) urlStr = "https://" + urlStr;
      try {
        new URL(urlStr);
      } catch {
        continue;
      }
      try {
        const { data: preview } = await api.get(
          `/preview?url=${encodeURIComponent(urlStr)}`
        );
        const payload = {
          deck_id: activeDeckId,
          name: (preview.site_name || preview.title || urlStr).slice(0, 40),
          url: preview.url || urlStr,
          style: "rich",
          size: "2x1",
          color: preview.theme_color || "#1A1C29",
          text_color: "#FFFFFF",
        };
        if (preview.image) payload.preview_image = preview.image;
        if (preview.title) payload.preview_title = preview.title;
        if (preview.description) payload.preview_description = preview.description;
        if (preview.theme_color) payload.theme_color = preview.theme_color;
        if (preview.site_name) payload.site_name = preview.site_name;
        if (preview.favicon) payload.favicon = preview.favicon;

        const { data: tile } = await api.post("/tiles", payload);
        newTiles.push(tile);
        added += 1;
      } catch (e) {
        // continue with next url
      }
    }

    setDroppingCount(0);
    if (newTiles.length > 0) {
      setTiles((prev) => [...prev, ...newTiles]);
    }

    if (added === 0) {
      toast.error("No se pudo crear ningún botón", { id: toastId });
    } else if (added === urls.length) {
      toast.success(
        added === 1
          ? `"${newTiles[0].name}" añadido`
          : `${added} botones añadidos`,
        { id: toastId }
      );
    } else {
      toast.success(`${added}/${urls.length} botones añadidos`, { id: toastId });
    }
  };

  const handleEditTile = (tile) => {
    setEditingTile(tile);
    setTileEditorOpen(true);
  };

  const handleQuickDeleteTile = async (tile) => {
    if (!window.confirm(`¿Eliminar "${tile.name}"?`)) return;
    try {
      await api.delete(`/tiles/${tile.tile_id}`);
      setTiles((prev) => prev.filter((t) => t.tile_id !== tile.tile_id));
      toast.success("Botón eliminado");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const handleDuplicateTile = async (tile) => {
    try {
      const payload = {
        deck_id: tile.deck_id,
        name: `${tile.name} (copia)`,
        url: tile.url,
        icon: tile.icon,
        color: tile.color,
        text_color: tile.text_color,
        size: tile.size,
        style: tile.style,
      };
      ["image_url", "preview_image", "preview_title", "preview_description", "theme_color", "site_name", "favicon"].forEach((k) => {
        if (tile[k]) payload[k] = tile[k];
      });
      const { data } = await api.post("/tiles", payload);
      setTiles((prev) => [...prev, data]);
      toast.success("Botón duplicado");
    } catch (e) {
      toast.error("No se pudo duplicar");
    }
  };

  const handleTileSaved = (tile, mode) => {
    if (mode === "create") {
      setTiles((prev) => [...prev, tile]);
      toast.success("Botón creado");
    } else if (mode === "update") {
      setTiles((prev) => prev.map((t) => (t.tile_id === tile.tile_id ? tile : t)));
      toast.success("Botón actualizado");
    } else if (mode === "delete") {
      setTiles((prev) => prev.filter((t) => t.tile_id !== tile));
      toast.success("Botón eliminado");
    }
  };

  const handleAddDeck = () => {
    setEditingDeck(null);
    setDeckEditorOpen(true);
  };

  const handleEditDeck = (deck) => {
    setEditingDeck(deck);
    setDeckEditorOpen(true);
  };

  const handleDeleteDeck = async (deck) => {
    if (
      !window.confirm(
        `¿Eliminar la plataforma "${deck.name}" y todos sus botones?`
      )
    )
      return;
    try {
      await api.delete(`/decks/${deck.deck_id}`);
      setDecks((prev) => {
        const next = prev.filter((d) => d.deck_id !== deck.deck_id);
        if (activeDeckId === deck.deck_id) {
          setActiveDeckId(next[0]?.deck_id || null);
        }
        return next;
      });
      toast.success("Plataforma eliminada");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const handleDeckSaved = (deck, mode) => {
    if (mode === "create") {
      setDecks((prev) => [...prev, deck]);
      setActiveDeckId(deck.deck_id);
      toast.success("Plataforma creada");
    } else if (mode === "update") {
      setDecks((prev) => prev.map((d) => (d.deck_id === deck.deck_id ? deck : d)));
      toast.success("Plataforma actualizada");
    } else if (mode === "delete") {
      setDecks((prev) => {
        const next = prev.filter((d) => d.deck_id !== deck);
        if (activeDeckId === deck) {
          setActiveDeckId(next[0]?.deck_id || null);
        }
        return next;
      });
      toast.success("Plataforma eliminada");
    }
  };

  const handleReorder = async (newTiles) => {
    setTiles(newTiles);
    try {
      await api.post("/tiles/reorder", {
        items: newTiles.map((t, idx) => ({ tile_id: t.tile_id, position: idx })),
      });
    } catch (e) {
      toast.error("No se pudo reordenar");
    }
  };

  // ---- Global DnD: tile reorder + tile-to-deck move ----
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [activeDragId, setActiveDragId] = useState(null);
  const isDraggingDeck =
    typeof activeDragId === "string" && activeDragId.startsWith("deck-");
  const activeDragTile = !isDraggingDeck
    ? tiles.find((t) => t.tile_id === activeDragId)
    : null;
  const activeDragDeck = isDraggingDeck
    ? decks.find((d) => `deck-${d.deck_id}` === activeDragId)
    : null;

  const handleDragStart = (e) => setActiveDragId(e.active.id);

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    setActiveDragId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // ---- DECK REORDER (active is a deck) ----
    if (activeId.startsWith("deck-")) {
      if (!overId.startsWith("deck-")) return;
      if (activeId === overId) return;
      const fromDeckId = activeId.substring(5);
      const toDeckId = overId.substring(5);
      const oldIdx = decks.findIndex((d) => d.deck_id === fromDeckId);
      const newIdx = decks.findIndex((d) => d.deck_id === toDeckId);
      if (oldIdx === -1 || newIdx === -1) return;
      const newDecks = arrayMove(decks, oldIdx, newIdx);
      setDecks(newDecks);
      try {
        await api.post("/decks/reorder", {
          items: newDecks.map((d, idx) => ({ deck_id: d.deck_id, position: idx })),
        });
      } catch (err) {
        toast.error("No se pudo reordenar las plataformas");
      }
      return;
    }

    // ---- TILE DRAG ----
    // Drop on a deck (sidebar) → move tile to that deck
    if (overId.startsWith("deck-")) {
      const targetDeckId = overId.substring(5);
      if (targetDeckId === activeDeckId) return;
      const tileId = active.id;
      const movingTile = tiles.find((t) => t.tile_id === tileId);
      if (!movingTile) return;
      // Optimistic remove
      setTiles((prev) => prev.filter((t) => t.tile_id !== tileId));
      try {
        await api.put(`/tiles/${tileId}`, { deck_id: targetDeckId });
        const targetDeck = decks.find((d) => d.deck_id === targetDeckId);
        toast.success(`"${movingTile.name}" movido a ${targetDeck?.name || "otra plataforma"}`);
      } catch (err) {
        // Rollback on error
        setTiles((prev) => [...prev, movingTile]);
        toast.error("No se pudo mover");
      }
      return;
    }

    // Otherwise, sortable reorder within current deck
    if (active.id === over.id) return;
    const oldIdx = tiles.findIndex((t) => t.tile_id === active.id);
    const newIdx = tiles.findIndex((t) => t.tile_id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newArr = arrayMove(tiles, oldIdx, newIdx);
    handleReorder(newArr);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg">
        <div className="text-white/70 font-mono text-sm tracking-widest">
          LOADING<span className="animate-pulse">...</span>
        </div>
      </div>
    );
  }

  return (
    <TourProvider>
    <DndContext
      sensors={dndSensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="relative flex min-h-screen bg-deck-bg text-white">
        <div className="noise-overlay" />

        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          decks={decks}
          activeDeckId={activeDeckId}
          onSelectDeck={setActiveDeckId}
          onAddDeck={handleAddDeck}
          onEditDeck={handleEditDeck}
          onDeleteDeck={handleDeleteDeck}
          isDraggingTile={!!activeDragTile}
        />

      <div className="flex-1 flex flex-col min-w-0 relative z-[1]">
        <Topbar
          user={user}
          deckName={activeDeck?.name || ""}
          view={view}
          onViewChange={setView}
          onSearchClick={() => setSearchOpen(true)}
          onPasteCreate={handlePasteAndCreate}
          onShareClick={() => setShareOpen(true)}
          pasting={pasting}
        />

        <main className="flex-1 px-6 md:px-10 py-6 md:py-10 overflow-auto" data-testid="main-area">
          {!activeDeck ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              <div className="text-2xl font-heading font-bold mb-2">
                No tienes plataformas todavía
              </div>
              <div className="text-white/50 mb-8 font-mono text-sm">
                Crea tu primera plataforma para empezar.
              </div>
              <button
                data-testid="empty-add-deck-btn"
                onClick={handleAddDeck}
                className="px-6 py-3 rounded-2xl bg-[#2563EB] text-white font-heading font-bold deck-tile flex items-center gap-2"
              >
                <Plus size={18} weight="bold" /> Nueva plataforma
              </button>
            </div>
          ) : (
            <DeckGrid
              tiles={tiles}
              view={view}
              onAddTile={handleAddTile}
              onEditTile={handleEditTile}
              onDeleteTile={handleQuickDeleteTile}
              onDuplicateTile={handleDuplicateTile}
            />
          )}
        </main>
      </div>

      <TileEditor
        open={tileEditorOpen}
        onOpenChange={setTileEditorOpen}
        tile={editingTile}
        deckId={activeDeckId}
        onSaved={handleTileSaved}
      />

      <DeckEditor
        open={deckEditorOpen}
        onOpenChange={setDeckEditorOpen}
        deck={editingDeck}
        onSaved={handleDeckSaved}
      />

      <SearchCommand
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onSelectTile={(tile) => {
          if (tile.deck_id !== activeDeckId) setActiveDeckId(tile.deck_id);
          window.open(tile.url, "_blank", "noopener,noreferrer");
        }}
        onSelectDeck={(deck) => {
          setActiveDeckId(deck.deck_id);
        }}
      />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        deckName={activeDeck?.name || ""}
      />

      <DropZone
        enabled={!!activeDeckId}
        onDropUrls={handleDropUrls}
        processing={droppingCount > 0}
      />

      <DragOverlay>
        {activeDragTile ? (
          <div className="drag-overlay" style={{ width: 160, height: 160 }}>
            <TileCard
              tile={activeDragTile}
              view="grid"
              onEdit={() => {}}
              onDelete={() => {}}
              onDuplicate={() => {}}
            />
          </div>
        ) : activeDragDeck ? (
          <DeckDragPreview deck={activeDragDeck} />
        ) : null}
      </DragOverlay>
      </div>
      <TourOverlay />
    </DndContext>
    </TourProvider>
  );
}
